/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useContext } from 'react';
import styled from 'styled-components';
import {
    createUIElement,
    KeyCode,
    Overlay,
    OVERLAY_CONTENT_MOD,
    OVERLAY_VIEW,
    SPACE_FILL,
    STACK_DIRECTION,
    StackView,
    useZIndexManager,
    VIEW_BG_VIEW,
} from '@veeam/components';
import { PageWizardPanelContext } from '@veeam/components/lib/PageWizard/context/PageWizardPanelContext';
import { PageWizardPanelProvider } from '@veeam/components/lib/PageWizard/context/providers/PageWizardPanelProvider';
import { PageWizardFooter } from '@veeam/components/lib/PageWizard/PageWizardFooter';
import { PageWizardProgress } from '@veeam/components/lib/PageWizard/WizardProgress';
import { WizardPanelContainer } from '@veeam/components/lib/WizardPanelContainer';

import type { Styleable, Testable, INDENT } from '@veeam/components';

import { PageDialogBody } from '../PageDialogBody/PageDialogBody';

export interface PageDialogLayoutProps extends Styleable, Testable {
    /**
     * Header slot
     */
    header: React.ReactElement;
    /**
     * Tabs slot
     */
    tabs: React.ReactElement;
    /**
     * Step content slot
     */
    content: React.ReactElement;
    /**
     * Footer slot
     */
    footer: React.ReactElement;
    /**
     * Loading state with blocking content
     */
    loading?: boolean;
    /**
     * Loading state without blocking content
     */
    pending?: boolean;
    /**
     * Side panel slot
     */
    panel?: React.ReactElement | null | false;
    /**
     * Keyboard on key press event handler
     */
    onKeyPress?: (e: KeyboardEvent) => void;
    /**
     * Request close handler, for example by press "ESC"
     */
    onRequestClose?: () => void;
    /**
     * Panel custom min width as CSS width value
     */
    panelMinWidth?: string;
    /**
     * Panel custom width as CSS width value
     */
    panelWidth?: string;
    /**
     * Is panel shrinkable. With this property you can autosize panel by content
     */
    shrinkPanel?: boolean;
    /**
     * Content full width size
     */
    fullWidthContent?: boolean;
    /**
     * Width of step content slot
     */
    widthContent?: string;

    bodyIndentTop?: INDENT;
    bodyIndentRight?: INDENT;
    bodyIndentBottom?: INDENT;
    bodyIndentLeft?: INDENT;
}

const Body = styled(StackView)`
    position: relative;
    flex: 1;
    overflow: hidden;
`;

const Inner = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
    flex-direction: column;
    position: relative;
`;

function PanelContainer({
    panel,
    panelMinWidth,
    panelWidth,
    shrinkPanel,
}: {
    panel?: React.ReactElement | null | false;
    panelMinWidth?: string;
    panelWidth?: string;
    shrinkPanel?: boolean;
}) {
    const panelApi = useContext(PageWizardPanelContext);
    panel = panel || panelApi.element;

    if (!panel) {
        return null;
    }

    return (
        <Overlay view={OVERLAY_VIEW.tone} contentMod={OVERLAY_CONTENT_MOD.right}>
            <WizardPanelContainer
                panel={panel}
                panelMinWidth={panelMinWidth}
                panelWidth={panelWidth}
                shrinkPanel={shrinkPanel || panelApi.isShrink}
            />
        </Overlay>
    );
}

export const PageDialogLayout = createUIElement('PageDialogLayout', (
    {
        className,
        header,
        tabs,
        content,
        footer,
        loading,
        panel,
        panelMinWidth = '768px',
        panelWidth = '50%',
        shrinkPanel = false,
        pending,
        fullWidthContent,
        widthContent = '800px',
        bodyIndentTop,
        bodyIndentRight,
        bodyIndentBottom,
        bodyIndentLeft,
        onRequestClose,
        onKeyPress,
        style,
        ...rest
    }: PageDialogLayoutProps,
    ref: React.RefObject<HTMLElement>,
) => {
    useZIndexManager({
        onKeyPress: (e: KeyboardEvent) => {
            if (e.keyCode === KeyCode.Escape) {
                onRequestClose?.();
            }
            onKeyPress?.(e);
        },
    });

    return (
        <PageWizardPanelProvider>
            <StackView
                {...rest}
                testId={rest['data-test-id'] || 'pagedialog-layout'}
                className={`vm-PageWizardLayout ${className}`}
                tabIndex={-1}
                direction={STACK_DIRECTION.column}
                spaceFill={SPACE_FILL.all}
                backgroundView={VIEW_BG_VIEW.normal}
                ref={ref}
                style={style}
            >
                {header}

                <Inner>
                    <Body>
                        {tabs}

                        <PageDialogBody
                            widthContent={widthContent}
                            fullWidthContent={fullWidthContent}
                            bodyIndentTop={bodyIndentTop}
                            bodyIndentRight={bodyIndentRight}
                            bodyIndentBottom={bodyIndentBottom}
                            bodyIndentLeft={bodyIndentLeft}
                        >
                            {content}
                        </PageDialogBody>

                        {(loading || pending) && <PageWizardProgress block={loading} />}
                    </Body>

                    {footer && (
                        <PageWizardFooter
                            widthContent={widthContent}
                            fullWidthContent={fullWidthContent}
                        >
                            {footer}
                        </PageWizardFooter>
                    )}

                    <PanelContainer
                        panel={panel}
                        panelMinWidth={panelMinWidth}
                        panelWidth={panelWidth}
                        shrinkPanel={shrinkPanel}
                    />
                </Inner>
            </StackView>
        </PageWizardPanelProvider>
    );
});
